import { useState, useEffect } from "react";
import {
  Building2, CheckCircle2, XCircle, Clock, Shield, Eye,
  Loader2, Users, AlertCircle, ShieldCheck, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface AccessRequest {
  id: string;
  ca_user_id: string;
  company_name: string;
  company_gstin: string | null;
  access_level: string;
  permissions: string[];
  status: string;
  requested_at: string;
  approved_at: string | null;
  notes: string | null;
}

const PERMISSION_LABELS: Record<string, string> = {
  view_accounting: "View Accounting",
  approve_entries: "Approve Entries",
  manage_gst: "Manage GST",
  generate_reports: "Generate Reports",
};

const ACCESS_LEVEL_LABELS: Record<string, string> = {
  view_only: "View Only",
  accounting: "Accounting Access",
  gst_filing: "GST Filing Access",
  full_financial: "Full Financial Access",
  full: "Full Access",
  readonly: "Read Only",
};

const statusConfig: Record<string, { label: string; cls: string }> = {
  pending: { label: "Pending", cls: "bg-yellow-500/10 text-yellow-600" },
  approved: { label: "Approved", cls: "bg-green-500/10 text-green-600" },
  rejected: { label: "Rejected", cls: "bg-destructive/10 text-destructive" },
  revoked: { label: "Revoked", cls: "bg-muted text-muted-foreground" },
};

const CAAccessApprovalsPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReq, setSelectedReq] = useState<AccessRequest | null>(null);
  const [rejectionNote, setRejectionNote] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (user) fetchRequests();
  }, [user]);

  const fetchRequests = async () => {
    setLoading(true);
    // Company admins see requests targeted to them
    // For demo, show all requests 
    const { data } = await supabase
      .from("ca_company_access")
      .select("*")
      .order("requested_at", { ascending: false });
    if (data) setRequests(data as AccessRequest[]);
    setLoading(false);
  };

  const handleAction = async (id: string, status: string) => {
    setProcessing(true);
    const updates: any = {
      status,
      company_admin_user_id: user?.id,
    };
    if (status === "approved") updates.approved_at = new Date().toISOString();
    if (status === "rejected" && rejectionNote.trim()) {
      updates.notes = rejectionNote.trim();
    }

    const { error } = await supabase
      .from("ca_company_access")
      .update(updates)
      .eq("id", id);

    if (error) {
      toast({ title: "Action failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `Request ${status}`, description: `CA access has been ${status}.` });
      setSelectedReq(null);
      setRejectionNote("");
      fetchRequests();
    }
    setProcessing(false);
  };

  const pending = requests.filter((r) => r.status === "pending");
  const active = requests.filter((r) => r.status === "approved");
  const others = requests.filter((r) => !["pending", "approved"].includes(r.status));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
          <Shield className="h-6 w-6 text-accent" />
          CA Access Management
        </h1>
        <p className="text-sm text-muted-foreground">Review and manage CA access requests to your company</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{pending.length}</p>
              <p className="text-xs text-muted-foreground">Pending Requests</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{active.length}</p>
              <p className="text-xs text-muted-foreground">Active CAs</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <Users className="h-5 w-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{requests.length}</p>
              <p className="text-xs text-muted-foreground">Total Requests</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Requests */}
      {pending.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              Pending Requests ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead>Access Level</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium text-foreground">{r.company_name}</TableCell>
                    <TableCell>
                      {r.company_gstin ? <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded">{r.company_gstin}</code> : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px]">{ACCESS_LEVEL_LABELS[r.access_level] || r.access_level}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.permissions.map((p) => (
                          <Badge key={p} variant="outline" className="text-[10px]">{PERMISSION_LABELS[p] || p}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(r.requested_at).toLocaleDateString("en-IN")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1.5 justify-end">
                        <Button size="sm" className="h-7 text-xs gap-1" onClick={() => handleAction(r.id, "approved")}>
                          <CheckCircle2 className="h-3 w-3" /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-destructive" onClick={() => setSelectedReq(r)}>
                          <XCircle className="h-3 w-3" /> Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Active CAs */}
      <Card className="bg-card border-border">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            Active CAs ({active.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {active.length === 0 ? (
            <p className="text-xs text-muted-foreground py-6 text-center">No active CA access yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead>Access Level</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Approved</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {active.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium text-foreground">{r.company_name}</TableCell>
                    <TableCell>
                      {r.company_gstin ? <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded">{r.company_gstin}</code> : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px]">{ACCESS_LEVEL_LABELS[r.access_level] || r.access_level}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.permissions.map((p) => (
                          <Badge key={p} variant="outline" className="text-[10px]">{PERMISSION_LABELS[p] || p}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.approved_at ? new Date(r.approved_at).toLocaleDateString("en-IN") : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-destructive" onClick={() => handleAction(r.id, "revoked")}>
                        <XCircle className="h-3 w-3" /> Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Rejection Dialog */}
      <Dialog open={!!selectedReq} onOpenChange={() => setSelectedReq(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reject Access Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-muted-foreground">
              Rejecting access for <strong>{selectedReq?.company_name}</strong>
            </p>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea
                value={rejectionNote}
                onChange={(e) => setRejectionNote(e.target.value)}
                placeholder="Provide a reason for rejection..."
                rows={3}
                className="mt-1 text-xs"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                className="flex-1 gap-1"
                onClick={() => selectedReq && handleAction(selectedReq.id, "rejected")}
                disabled={processing}
              >
                {processing ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                Reject
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setSelectedReq(null)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {loading && (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
};

export default CAAccessApprovalsPage;
